import { Component, EventEmitter, Input, OnInit, Output, OnChanges, SimpleChanges, OnDestroy } from '@angular/core';

import { ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';
import { AlertNotificationService } from 'src/app/services/alert-notification.service';
import { LoaderService } from 'src/app/services/loader.service';
import { ProjectsService } from 'src/app/services/api/projects.service';
import { ConfirmationDialogService } from 'src/app/services/confirmation-dialog.service';
import { LoaderComponent } from 'src/app/components/widgets/loader/loader.component';
import { CommonService } from 'src/app/services/common.service';
import { environment } from 'src/environments/environment';

const HASH_MISMATCH_DESCRIPTION = 'File hash not matching with uploaded file';

@Component({
  selector: 'projects-contracts-docs',
  imports: [
    ReactiveFormsModule,
    LoaderComponent
],
  templateUrl: './projects-contracts-docs.component.html',
  styleUrl: './projects-contracts-docs.component.scss'
})
export class ProjectsContractsDocsComponent implements OnInit, OnChanges, OnDestroy {
  @Input() projectId?: string;
  @Output() onContractsDocsUpdated = new EventEmitter<any>();
  
  documents: any[] = [];
  readonly LOADER_CONTEXT = 'projects-contracts-docs';
  isLoading: boolean = false;
  private subscription = new Subscription();

  uploadForm: FormGroup;
  selectedFile: File | null = null;
  
  documentToDelete: any = null;

  constructor(
    private fb: FormBuilder,
    private alertNotificationService: AlertNotificationService,
    private projectsService: ProjectsService,
    private loaderService: LoaderService,
    private confirmationDialogService: ConfirmationDialogService,
    private commonService: CommonService
  ) {
    this.uploadForm = this.fb.group({
      name: ['', Validators.required]
    });

    this.subscription.add(
      this.loaderService.getLoadingAsObservable(this.LOADER_CONTEXT).subscribe(loading => {
        this.isLoading = loading;
      })
    );
  }

  ngOnInit(): void {
    if (this.projectId) {
      this.loadDocuments();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['projectId'] && !changes['projectId'].firstChange) {
      this.loadDocuments();
    }
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }
  
  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    
    if (input.files && input.files.length) {
      this.selectedFile = input.files[0];
      
      if (!this.uploadForm.get('name')?.value) {
        const fileName = this.selectedFile.name.split('.')[0];
        this.uploadForm.get('name')?.setValue(fileName);
      }
    }
  }
  
  async uploadDocument(): Promise<void> {
    if (this.uploadForm.valid && this.selectedFile && this.projectId) {
      if (!this.commonService.isWebCryptoAvailable()) {
        this.alertNotificationService.error('This browser cannot verify file integrity. Please use an updated browser.');
        return;
      }

      this.loaderService.startLoading(this.LOADER_CONTEXT);

      const formData = new FormData();
      const data = {
        description: this.uploadForm.get('name')?.value,
      };

      formData.append('data', JSON.stringify(data));
      formData.append('file', this.selectedFile, this.selectedFile.name);

      const hashHex = await this.commonService.computeSha256Hex(this.selectedFile);
      formData.append('hash', hashHex);
      formData.append('hashAlg', environment.fileHashAlgorithm);

      this.projectsService.createProjectContractAndDocs(+this.projectId, formData).subscribe({
        next: (response) => {
          this.alertNotificationService.success('Document uploaded successfully');
          this.uploadForm.reset();
          this.selectedFile = null;

          const fileInput = document.getElementById('documentFile') as HTMLInputElement;
          if (fileInput) {
            fileInput.value = '';
          }

          this.loadDocuments();

          this.onContractsDocsUpdated.emit({
            type: 'contractsDocs',
            action: 'upload',
            documentId: response.data?.projectContractId,
            status: 'uploaded'
          });
        },
        error: (error) => {
          console.error('Error uploading document:', error);
          if (error?.error?.error?.description === HASH_MISMATCH_DESCRIPTION) {
            this.alertNotificationService.error('The uploaded file failed the integrity check. Please upload the file again.');
          } else {
            this.alertNotificationService.error('Error uploading document');
          }
          this.loaderService.stopLoading(this.LOADER_CONTEXT);
        },
        complete: () => {
          this.loaderService.stopLoading(this.LOADER_CONTEXT);
        }
      });
    }
  }

  loadDocuments(): void {
    if (!this.projectId) return;
    
    this.loaderService.startLoading(this.LOADER_CONTEXT);
    
    this.projectsService.listProjectContractsAndDocs(+this.projectId).subscribe({
      next: (response) => {
        this.documents = response.data?.projectContracts || [];
        
        this.onContractsDocsUpdated.emit({
          type: 'contractsDocs',
          count: this.documents.length,
          status: 'loaded'
        });
      },
      error: (error) => {
        console.error('Error loading documents:', error);
        this.alertNotificationService.error('Error loading documents');
        this.documents = [];
        this.loaderService.stopLoading(this.LOADER_CONTEXT);
      },
      complete: () => {
        this.loaderService.stopLoading(this.LOADER_CONTEXT);
      }
    });
  }

  downloadDocument(document: any): void {
    if (!this.projectId || !document.projectContractId) return;
    
    if (document.attachments) {
      this.loaderService.stopLoading(this.LOADER_CONTEXT);
      this.projectsService.GET(document.attachments[0].url).subscribe({
        next: (response:any) => {
          window.open(response.data.attachment.publicUrl, '_blank');
        }, 
        error: (error:any) => {
          this.alertNotificationService.error('Error downloading file');
          console.error('Error:', error);
        }
      })
    }
  }

  deleteDocument(document: any): void {
    this.documentToDelete = document;
    this.confirmationDialogService.open(
      `Are you sure you want to delete the document <strong>${document.description}</strong>?`,
      () => this.onConfirmDelete()
    );
  }
  
  onConfirmDelete(): void {
    if (this.documentToDelete && this.projectId) {
      this.loaderService.startLoading(this.LOADER_CONTEXT);
      
      this.projectsService.deleteProjectContractAndDocs(+this.projectId, this.documentToDelete.projectContractId).subscribe({
        next: () => {
          this.alertNotificationService.success('Document deleted successfully');
          
          this.onContractsDocsUpdated.emit({
            type: 'contractsDocs',
            action: 'delete',
            documentId: this.documentToDelete.projectContractId,
            status: 'deleted'
          });
          
          this.loadDocuments();
          this.closeConfirmationDialog();
        },
        error: (error) => {
          console.error('Error deleting document:', error);
          this.alertNotificationService.error('Error deleting document');
          this.loaderService.stopLoading(this.LOADER_CONTEXT);
          this.closeConfirmationDialog();
        },
        complete: () => {
          this.loaderService.stopLoading(this.LOADER_CONTEXT);
        }
      });
    }
  }
  
  closeConfirmationDialog(): void {
    this.documentToDelete = null;
  }
}
